import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  ArrowLeft, ShoppingBag, Calendar, 
  CreditCard, Banknote, Trash2, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { currentUser, currentBranch } = useApp();
  
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const normalizeOrders = (payload) => {
    if (Array.isArray(payload)) {
      return payload.map((order) => ({
        ...order,
        items: Array.isArray(order.items) ? order.items : []
      }));
    }

    if (payload && Array.isArray(payload.value)) {
      return payload.value.map((order) => ({
        ...order,
        items: Array.isArray(order.items) ? order.items : []
      }));
    }

    return [];
  };

  const fetchOrders = useCallback(async () => {
    if (!currentUser) return;
    
    setIsLoading(true);
    try {
      // Add cache busting parameter to prevent stale data
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/orders?staff_id=${currentUser.id}&_t=${timestamp}`);
      setOrders(normalizeOrders(response.data));
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    fetchOrders();
  }, [currentUser, navigate, fetchOrders]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDeleteClick = (order) => {
    setSelectedOrder(order);
    setDeleteDialog(true);
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      const response = await axios.delete(`${API}/orders/${selectedOrder.id}`);
      console.log('Delete response:', response.data);
      
      // Immediately update local state to remove the deleted order
      setOrders(prevOrders => prevOrders.filter(order => order.id !== selectedOrder.id));
      
      setDeleteDialog(false);
      setSelectedOrder(null);
      
      // Also refetch from server to ensure sync
      setTimeout(() => fetchOrders(), 500);
    } catch (err) {
      console.error('Failed to delete order:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Request URL:', `${API}/orders/${selectedOrder.id}`);
      alert(`Nepodařilo se smazat objednávku: ${err.response?.data?.error || err.message}`);
    }
  };

  const branchName = currentBranch === 'smichov' ? 'Smíchov' : 'Stodůlky';

  if (!currentUser) return null;

  return (
    <div className="min-h-screen bg-obsidian" data-testid="my-orders-page">
      {/* Header */}
      <header className="bg-paper border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/pos')}
            className="text-muted-foreground hover:text-gold"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-gold" />
            <div>
              <h1 className="text-xl font-semibold text-white">Moje objednávky</h1>
              <p className="text-sm text-muted-foreground">{currentUser.name} - {branchName}</p>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          className="text-muted-foreground hover:text-destructive"
        >
          <X className="w-5 h-5" />
        </Button>
      </header>

      {/* Content */}
      <div className="container mx-auto p-4 max-w-6xl">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
            <p className="mt-4 text-muted-foreground">Načítání...</p>
          </div>
        ) : orders.length === 0 ? (
          <Card className="bg-paper border-white/10">
            <CardContent className="py-12 text-center">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-lg text-muted-foreground">Zatím nemáte žádné objednávky</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {orders.map((order) => (
              <Card key={order.id} className="bg-paper border-white/10">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gold" />
                          {formatDate(order.created_at)}
                        </div>
                      </CardTitle>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {order.payment_method === 'cash' ? (
                          <>
                            <Banknote className="w-4 h-4" />
                            <span>Hotově</span>
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4" />
                            <span>Kartou</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(order)}
                      className="border-destructive/50 text-destructive hover:bg-destructive hover:text-white"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-3">
                    {Array.isArray(order.items) && order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{item.service_name}</span>
                        <span className="font-mono">{formatPrice(item.price)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Dýška</span>
                      <span className="font-mono">{formatPrice(order.tip_amount || 0)}</span>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-white/10 flex justify-between items-center">
                    <span className="font-semibold">Celkem:</span>
                    <span className="text-2xl font-mono font-bold text-gold">{formatPrice(order.total)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-paper border border-destructive/30 max-w-md">
          <DialogHeader>
            <DialogTitle>Smazat objednávku</DialogTitle>
            <DialogDescription>Opravdu chcete smazat tuto objednávku? Tuto akci nelze vrátit zpět.</DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="py-4">
              <div className="space-y-2 mb-4">
                <div className="text-sm text-muted-foreground">
                  Datum: {formatDate(selectedOrder.created_at)}
                </div>
                {Array.isArray(selectedOrder.items) && selectedOrder.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.service_name}</span>
                    <span className="font-mono">{formatPrice(item.price)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm">
                  <span>Dýška</span>
                  <span className="font-mono">{formatPrice(selectedOrder.tip_amount || 0)}</span>
                </div>
                <div className="pt-2 border-t flex justify-between font-semibold">
                  <span>Celkem:</span>
                  <span className="text-gold font-mono">{formatPrice(selectedOrder.total)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => setDeleteDialog(false)}>
                  Zrušit
                </Button>
                <Button 
                  onClick={handleDeleteOrder} 
                  className="bg-destructive text-white hover:bg-destructive/90"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Smazat
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyOrdersPage;
